import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

interface Activity {
  id: number;
  type: 'sale' | 'stock' | 'payment' | 'purchase' | 'credit' | 'other';
  title: string;
  description: string;
  created_at: string; // ISO date from API
}

export default function RecentActivities() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Mapping type → icône + couleur
  const getActivityStyle = (type: string) => {
    switch (type) {
      case 'sale':
        return { icon: 'ri-shopping-cart-line', color: 'text-green-600 bg-green-50' };
      case 'stock':
        return { icon: 'ri-alert-line', color: 'text-orange-600 bg-orange-50' };
      case 'payment':
        return { icon: 'ri-money-dollar-circle-line', color: 'text-blue-600 bg-blue-50' };
      case 'purchase':
        return { icon: 'ri-truck-line', color: 'text-purple-600 bg-purple-50' };
      case 'credit':
        return { icon: 'ri-calendar-line', color: 'text-red-600 bg-red-50' };
      default:
        return { icon: 'ri-notification-line', color: 'text-gray-600 bg-gray-50' };
    }
  };

  // Formatage de la date relative (ex: "Il y a 15 minutes")
  const formatRelativeTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: fr,
    });
  };

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await getResources('activities/recent');
        
        if (response.data.success) {
          setActivities(response.data.data.slice(0, 10)); // Limite à 10 pour le dashboard
        }
      } catch (error) {
        console.error('Erreur chargement activités récentes', error);
        // Fallback statique en cas d'erreur
        setActivities([
          {
            id: 1,
            type: 'sale',
            title: 'Nouvelle vente - Client SARL DIALLO',
            description: 'Facture N°FAC-2024-0156 - 125 000 FCFA',
            created_at: new Date().toISOString(),
          },
          {
            id: 2,
            type: 'stock',
            title: 'Alerte stock faible',
            description: 'Riz parfumé 25kg - Stock: 12 unités',
            created_at: new Date(Date.now() - 3600000).toISOString(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Activités Récentes</h3>
          <button
            onClick={() => navigate('/activities')}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Voir tout
          </button>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start space-x-4 p-3 animate-pulse">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : activities.length > 0 ? (
          <div className="space-y-4">
            {activities.map((activity) => {
              const style = getActivityStyle(activity.type);

              return (
                <div
                  key={activity.id}
                  className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    // Optionnel : navigation vers détail selon type
                    // ex: navigate(`/sales/${activity.reference}`)
                  }}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${style.color}`}>
                    <i className={`${style.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 mb-1">{activity.title}</p>
                    <p className="text-sm text-gray-600 mb-1">{activity.description}</p>
                    <p className="text-xs text-gray-500">{formatRelativeTime(activity.created_at)}</p>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <i className="ri-more-2-line"></i>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <i className="ri-history-line text-4xl mb-4"></i>
            <p>Aucune activité récente</p>
          </div>
        )}
      </div>
    </div>
  );
}